import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, Coins, Users2, Scale, Warehouse, Hammer, ClipboardCheck } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Cell } from "recharts";
import { AdminShell } from "@/components/admin/AdminShell";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, formatINR, formatNum } from "@/lib/api";
import { ReconciliationTab } from "@/pages/admin/reports/ReconciliationTab";
import { SupplierRegisterTab } from "@/pages/admin/reports/SupplierRegisterTab";
import { ProfitMarginsTab } from "@/pages/admin/reports/ProfitMarginsTab";
import { hasAdminSession } from "@/lib/session";

const CHART_COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

function monthLabel(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
}

// Compact axis label for large rupee values (e.g. ₹1.5L, ₹42k) — extracted so the
// same lakh/thousand formatting logic isn't duplicated as an inline nested ternary
// on every chart's Y-axis tickFormatter.
function formatAxisValue(v) {
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(0)}k`;
  return `₹${v}`;
}

function ChartTooltip({ active, payload, label, valueFormatter }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg border border-border bg-[hsl(var(--popover))] px-3 py-2 shadow-[0_1px_0_rgba(27,20,22,0.06),0_10px_30px_rgba(27,20,22,0.06)] text-xs">
      <div className="font-medium mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={p.dataKey || p.name || i} className="text-muted-foreground">{p.name}: <span className="font-mono text-foreground">{valueFormatter ? valueFormatter(p.value) : p.value}</span></div>
      ))}
    </div>
  );
}

export default function Reports() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [goldBalance, setGoldBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const [{ data: a }, { data: g }] = await Promise.all([
        api.get("/purchase/reports/analytics", { params: { months: 12 } }),
        api.get("/purchase/reports/gold-balance"),
      ]);
      setAnalytics(a);
      setGoldBalance(g);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasAdminSession()) { navigate("/admin/login"); return; }
    load();
    // eslint-disable-next-line
  }, []);

  const trend = (analytics?.monthly_trend || []).map((r) => ({ ...r, label: monthLabel(r.month) }));
  const topSuppliers = analytics?.top_suppliers || [];
  const breakdown = goldBalance?.breakdown || [];

  return (
    <AdminShell title="Reports">
      <Tabs defaultValue="analytics" className="mb-4">
        <TabsList data-testid="reports-tabs">
          <TabsTrigger data-testid="reports-tab-analytics" value="analytics">Analytics</TabsTrigger>
          <TabsTrigger data-testid="reports-tab-register" value="register">Purchase Register</TabsTrigger>
          <TabsTrigger data-testid="reports-tab-reconciliation" value="reconciliation">Central Reconciliation</TabsTrigger>
          <TabsTrigger data-testid="reports-tab-margins" value="margins">Detailed Margins</TabsTrigger>
        </TabsList>

        <TabsContent value="analytics" className="mt-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Card className="p-4" data-testid="report-kpi-total-fine-gold">
          <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">Total Fine Gold</span><Scale className="h-4 w-4 text-muted-foreground" /></div>
          <div className="font-mono text-2xl mt-2">{loading ? <Skeleton className="h-7 w-20" /> : `${formatNum(goldBalance?.total_fine_gold_g)}g`}</div>
        </Card>
        <Card className="p-4" data-testid="report-kpi-gold-in-stock">
          <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">Gold In Stock</span><Warehouse className="h-4 w-4 text-muted-foreground" /></div>
          <div className="font-mono text-2xl mt-2">{loading ? <Skeleton className="h-7 w-20" /> : `${formatNum(goldBalance?.gold_in_stock_g)}g`}</div>
        </Card>
        <Card className="p-4" data-testid="report-kpi-gold-with-karigars">
          <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">With Karigars</span><Hammer className="h-4 w-4 text-muted-foreground" /></div>
          <div className="font-mono text-2xl mt-2">{loading ? <Skeleton className="h-7 w-20" /> : `${formatNum(goldBalance?.gold_with_karigars_g)}g`}</div>
        </Card>
        <Card className="p-4" data-testid="report-kpi-monthly-purchase-value">
          <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">This Month&apos;s Purchases</span><Coins className="h-4 w-4 text-muted-foreground" /></div>
          <div className="font-mono text-2xl mt-2">{loading ? <Skeleton className="h-7 w-24" /> : formatINR(trend[trend.length - 1]?.total || 0)}</div>
        </Card>
      </div>

      {error ? (
        <Card className="p-8 text-center" data-testid="reports-error-state">
          <p className="text-sm text-muted-foreground mb-3">Could not load reports right now.</p>
          <button data-testid="reports-retry-button" onClick={load} className="text-sm text-[hsl(var(--primary))] underline underline-offset-2">Retry</button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <Card className="p-4" data-testid="report-monthly-trend-chart">
            <div className="flex items-center gap-2 mb-3"><TrendingUp className="h-4 w-4 text-muted-foreground" /><span className="text-sm font-medium">Monthly Purchase Trend (12 months)</span></div>
            {loading ? <Skeleton className="h-64 w-full" /> : trend.every((t) => t.total === 0) ? (
              <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">No purchase history yet — record a purchase to see trends here.</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={trend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={{ stroke: "hsl(var(--border))" }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={70}
                    tickFormatter={formatAxisValue} />
                  <Tooltip content={<ChartTooltip valueFormatter={formatINR} />} />
                  <Line type="monotone" dataKey="total" name="Purchase Value" stroke={CHART_COLORS[0]} strokeWidth={2.5} dot={{ r: 3, fill: CHART_COLORS[0] }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>

          <Card className="p-4" data-testid="report-top-suppliers-chart">
            <div className="flex items-center gap-2 mb-3"><Users2 className="h-4 w-4 text-muted-foreground" /><span className="text-sm font-medium">Top Suppliers by Spend</span></div>
            {loading ? <Skeleton className="h-64 w-full" /> : topSuppliers.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">No supplier spend recorded yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={topSuppliers} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false}
                    tickFormatter={formatAxisValue} />
                  <YAxis type="category" dataKey="supplier_name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={90} />
                  <Tooltip content={<ChartTooltip valueFormatter={formatINR} />} />
                  <Bar dataKey="total_spend" name="Total Spend" radius={[0, 4, 4, 0]}>
                    {topSuppliers.map((s, i) => <Cell key={s.supplier_name || i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </div>
      )}

      {!error && (
        <Card className="p-4" data-testid="report-gold-balance-chart">
          <div className="flex items-center gap-2 mb-1"><ClipboardCheck className="h-4 w-4 text-muted-foreground" /><span className="text-sm font-medium">Gold Balance Report — Fine Gold Equivalent</span></div>
          <p className="text-xs text-muted-foreground mb-3">Where the business&apos;s fine gold physically sits right now: in stock, issued to karigars, or received back but awaiting reconciliation.</p>
          {loading ? <Skeleton className="h-56 w-full" /> : breakdown.every((b) => b.fine_gold_g === 0) ? (
            <div className="h-40 flex items-center justify-center text-sm text-muted-foreground">No gold movement recorded yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={breakdown} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={{ stroke: "hsl(var(--border))" }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}g`} />
                <Tooltip content={<ChartTooltip valueFormatter={(v) => `${formatNum(v)}g`} />} />
                <Bar dataKey="fine_gold_g" name="Fine Gold" radius={[4, 4, 0, 0]}>
                  {breakdown.map((b, i) => <Cell key={b.label || i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      )}
        </TabsContent>

        <TabsContent value="register" className="mt-4">
          <SupplierRegisterTab />
        </TabsContent>

        <TabsContent value="reconciliation" className="mt-4">
          <ReconciliationTab />
        </TabsContent>

        <TabsContent value="margins" className="mt-4">
          <ProfitMarginsTab />
        </TabsContent>
      </Tabs>
    </AdminShell>
  );
}
